import { Injectable, signal } from "@angular/core";

import {
  DataService as CoreDataService,
  ObjectKey,
  PersistenceService,
  PersistMode,
  PersistObjectModel,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryObjectsModel,
  TargetColumnValue,
  TargetObjectData,
} from "@barthauer/abc/core/data";

import { ObjectHelper } from "@barthauer/abc/core";
import { ValidationStatus } from "@barthauer/abc/ui-components";
import {
  GeoDsComment,
  Measure,
  Property,
  SubProject,
} from "@barthauer/custom/xmt-models";
import { DxNumberBoxComponent } from "devextreme-angular";
import { Column } from "devextreme/ui/data_grid";
import { BehaviorSubject, forkJoin, from, Observable, of } from "rxjs";
import { catchError, first, map, switchMap } from "rxjs/operators";
import { PersonStatus, ROLES } from "../../models/enumeration-list.model";
import { DataDefinitionOptions } from "../../models/home.model";
import { BasedRoleOPathService } from "../../services/based-role-opath.service";
import { DataService, ObjectKeys } from "../../services/data.service";
import { Persons, StuffMemberPerson } from "./measure.model";
import { IRating } from "./measure-detail/measurre-evaluation/measure-evaluation.component";
import {
  INotificationRecord,
  NotificationService1,
  NotificationSettingType,
  EmailNotificationDescriptions,
  EmailNotificationSubjects,
  EmailNotificationType,
} from "../../services/notification1.service";
import { Measures } from "./measure-list/measure-list.component";
import { AIMSROUTES } from "../../../app-routing.module";
import { LanguageService } from "../../services/language.service";
import { ObjectTypeService } from "../../services/object-type.service";

export class IMeasure extends Measure {
  comment?: GeoDsComment;
}
@Injectable({
  providedIn: "root",
})
export class MeasureService {
  public selectedMeasures: Measure[];
  public cachedColumnsResponse: Column[];
  public collaboratorsColumns: Column[] = [];
  public collaboratorsTotalTimeColumns: Column[] = [];
  private calculateTotalTime = new BehaviorSubject<boolean>(false);
  public calculateTotalTime$ = this.calculateTotalTime.asObservable();
  private newStatusReport = new BehaviorSubject<boolean>(false);
  public newStatusReport$ = this.newStatusReport.asObservable();
  public isCreatingSubTask: boolean;
  public staffMembersDataSource: StuffMemberPerson[] = [];
  public measureResponsibleDataSource: StuffMemberPerson[] = [];
  public currentMeasure = signal<IMeasure>(new IMeasure(null));

  public ROLES = ROLES;
  public degree = [
    { id: 0, value: "0%" },
    { id: 25, value: "25%" },
    { id: 50, value: "50%" },
    { id: 75, value: "75%" },
    { id: 100, value: "100%" },
  ];
  public currentMeasureId = signal<string | null>(null);
  public currentMeasureIndex: number = 0;
  public measureStatus = [
    { name: "Archived", value: "Erledigt" },
    { name: "Priorized", value: "In Arbeit" },
    { name: "Backlog", value: "Offen" },
  ];
  public maturityLevel: DataDefinitionOptions[] = [
    { name: "HG1", value: "HG1" },
    { name: "HG2", value: "HG2" },
    { name: "HG3", value: "HG3" },
    { name: "HG4", value: "HG4" },
    { name: "HG5", value: "HG5" },
  ];
  public statusPercentage: DataDefinitionOptions[] = [
    { name: "0%", value: 0 },
    { name: "20%", value: 20 },
    { name: "40%", value: 40 },
    { name: "60%", value: 60 },
    { name: "80%", value: 80 },
    { name: "100%", value: 100 },
  ];
  public readonly measureDesignationColumns = [
    this.coreDataService.createQueryColumn(
      "Id",
      "Id",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "Designation",
      "Designation",
      QueryColumnSortOrder.None,
    ),
  ];
  public allMeasures: Measures[];

  constructor(
    private dataService: DataService,
    private coreDataService: CoreDataService,
    private persistenceService: PersistenceService,
    private opathService: BasedRoleOPathService,
    private notificationService: NotificationService1,
    private languageService: LanguageService,
    private objectTypeService: ObjectTypeService,
  ) {}
  public isStrategieteamAndResponsible(idResponsible: string): boolean {
    return (
      this.dataService.userRole === this.ROLES.STRATEGIETEM &&
      idResponsible === sessionStorage.getItem("profileId")
    );
  }
  public calculateOverAllTime(measure) {
    const collaboratorsList = [];
    if (
      Array.isArray(measure?.MeasureTasks) &&
      measure.MeasureTasks.length > 0
    ) {
      measure.MeasureTasks.forEach((task) => {
        if (task.Collaborators?.length > 0) {
          task.Collaborators.forEach((collaborator) => {
            collaboratorsList.push(collaborator);
          });
        }
      });
    }
    const unStructuredCollaboratorList = ObjectHelper.groupByListItems(
      collaboratorsList,
      "IdRefMember",
      "TimePlanned",
    );
    const structuredCollaboratorList = [];
    Object.keys(unStructuredCollaboratorList).map(function (key) {
      const sum = unStructuredCollaboratorList[key].reduce((a, b) => a + b, 0);
      unStructuredCollaboratorList[key] = { name: key, sum: sum };
      structuredCollaboratorList.push(unStructuredCollaboratorList[key]);
    });
    return structuredCollaboratorList;
  }

  public realTimecalculateTotalTime(data: boolean) {
    this.calculateTotalTime.next(data);
  }
  public setNewSatusReport(statusReport: boolean) {
    this.newStatusReport.next(statusReport);
  }
  public validateNumberField(
    field: DxNumberBoxComponent,
    minVal: number,
    maxVal: number,
  ) {
    if (field && field.value) {
      if (field.value >= minVal && field.value <= maxVal) {
        field.validationStatus = ValidationStatus.Valid;
      } else {
        field.validationStatus = ValidationStatus.Invalid;
      }
    }
  }

  public updateMeasure(columnName: string, value, allowUpdate: boolean): void {
    if (allowUpdate === true) {
      const measurePersistQuery: TargetObjectData = new TargetObjectData();
      measurePersistQuery.ObjectKey = new ObjectKey();
      measurePersistQuery.ObjectKey.ObjectType = ObjectKeys.MEASURE;
      const measureColumns: TargetColumnValue[] = [
        { Name: columnName, Value: value },
      ];
      measurePersistQuery.Mode = PersistMode.Update;
      measurePersistQuery.ObjectKey.Id = this.currentMeasureId();
      measurePersistQuery.TargetColumns = measureColumns;
      const persistObject: PersistObjectModel = new PersistObjectModel();
      persistObject.Object = measurePersistQuery;
      if (columnName === "IdRefPersonResponsible") {
        this.persistenceService
          .executePersistObjectQuery(persistObject)
          .pipe(
            switchMap(() => {
              return this.notificationService.getNotificationSettings(value);
            }),
            switchMap((data: Property[]) => {
              const { notificationByEmail, notificationInApp, language } =
                this.notificationService.parseNotificationSettings(data);
              const inApp$ = notificationInApp
                ? this.notificationService.createNotifications([
                    {
                      ParentId: this.currentMeasureId(),
                      ParentObjectType: this.objectTypeService.getObjectTypesValue(ObjectKeys.MEASURE),
                      Comment: "notification.Measure-UserAssigned",
                      Text: "",
                      UserIdList: [value],
                    },
                  ])
                : of(null);
              const email$ = notificationByEmail
                ? this.dataService
                    .readObjects(
                      ObjectKeys.PERSON,
                      [
                        this.coreDataService.createQueryColumn(
                          "EMail",
                          "EMail",
                        ),
                      ],
                      `Id='${value}'`,
                    )
                    .pipe(
                      switchMap((persons: any[]) => {
                        const email = persons?.[0]?.EMail;
                        if (!email) return of(null);
                        return from(
                          this.languageService.translateBulk(
                            [
                              {
                                field: "subject",
                                key: EmailNotificationSubjects.MEASURE_USER_ASSIGNED,
                              },
                              {
                                field: "description",
                                key: EmailNotificationDescriptions.MEASURE_USER_ASSIGNED,
                                params: {
                                  0: this.currentMeasure()?.Designation
                                    ? this.currentMeasure()?.Designation
                                    : this.currentMeasure()?.Title,
                                },
                              },
                              {
                                field: "notificationType",
                                key: EmailNotificationType.MEASURE_USER_ASSIGNED,
                              },
                            ],
                            language,
                          ),
                        ).pipe(
                          switchMap((translations) => {
                            const redirectUrl =
                              this.dataService.generateRedirectionUrl(
                                AIMSROUTES.MEASURE_DETAIL,
                                [
                                  AIMSROUTES.MEASURES,
                                  this.currentMeasure()?.Id,
                                ],
                              );
                            const content = {
                              notificationType: translations.notificationType,
                              description: translations.description,
                              redirectUrl,
                            };
                            return this.notificationService.sendNotificationEmail(
                              content,
                              translations.subject,
                              email,
                              language,
                            );
                          }),
                        );
                      }),
                    )
                : of(null);
              return forkJoin([inApp$, email$]);
            }),
          )
          .subscribe();
      }
      this.persistenceService
        .executePersistObjectQuery(persistObject)
        .subscribe();
    }
  }
  public createNotifications(
    notificationRecords: INotificationRecord[],
  ): Observable<ArrayBuffer> {
    return this.notificationService.createNotifications(notificationRecords);
  }
  public getUsersWithSteeringComitteRole(): Observable<ArrayBuffer[]> {
    const addressId = sessionStorage.getItem("profileAddressId");
    const projectId = sessionStorage.getItem("projectId");
    const AddressPersonColumns = [
      this.coreDataService.createQueryColumn(
        "IdRefPerson",
        "IdRefPerson",
        QueryColumnSortOrder.None,
      ),
      this.coreDataService.createQueryColumn(
        "Person.Email",
        "Email",
        QueryColumnSortOrder.None,
      ),
      this.coreDataService.createQueryColumn(
        `iif(Exists(Person.Properties[Designation = '${NotificationSettingType.EMAIL}']),Person.Properties[Designation = '${NotificationSettingType.EMAIL}'].BoolValue <>   false, true)`,
        "EmailNotification",
      ),
      this.coreDataService.createQueryColumn(
        `Properties[Designation = '${NotificationSettingType.EMAIL}'].TextValue `,
        "Language",
      ),
      this.coreDataService.createQueryColumn(
        `iif(Exists(Person.Properties[Designation = '${NotificationSettingType.INAPP}']),Person.Properties[Designation = '${NotificationSettingType.INAPP}'].BoolValue <>   false, true)`,
        "InAppNotification",
      ),
    ];
    const addressPersonOPath = `ParentId='${projectId}'    And  Person.AddressPerson_IdRefPerson_Person.Status <> '${PersonStatus.ARCHIVED}' And Person.AddressPerson_IdRefPerson_Person.Position In ('${this.ROLES.SteeringCommittee}')  `;
    return this.dataService
      .readObjects(
        ObjectKeys.PROJECT_PERSON,
        AddressPersonColumns,
        addressPersonOPath,
      )
      .pipe(
        first(),
        map((users) => {
          if (Array.isArray(users) && users.length > 0) {
            return users;
          }
          return [];
        }),
        catchError((error) => {
          return of([]);
        }),
      );
  }
  public prepareMeasuresByProjectId(measureColumns: QueryColumn[]) {
    return this.dataService
      .readObjects<
        []
      >(ObjectKeys.MEASURE, [this.coreDataService.createQueryColumn("Id", "Id", QueryColumnSortOrder.None)])
      .pipe(
        switchMap(() => {
          const subProjectColumns = [
            this.coreDataService.createQueryColumn(
              "Id",
              "Id",
              QueryColumnSortOrder.None,
            ),
            this.coreDataService.createQueryColumn(
              "Designation",
              "Designation",
              QueryColumnSortOrder.None,
            ),
          ];
          const subProjectOPath =
            "ParentId=" + "'" + sessionStorage.getItem("projectId") + "'";
          return this.dataService
            .readObjects<
              SubProject[]
            >(ObjectKeys.SUBPROJECT, subProjectColumns, subProjectOPath)
            .pipe(
              switchMap(() => {
                const measureOPath = this.checkMeasureOPath();
                return this.dataService
                  .readObjects<
                    Measure[]
                  >(ObjectKeys.MEASURE, measureColumns, measureOPath)
                  .pipe(map((measures) => measures));
              }),
            );
        }),
      );
  }
  public checkMeasureOPath(): string {
    return this.opathService.checkMeasureOPath();
  }
  public prepareMeasures(measureColumns: QueryColumn[]) {
    const measureOPath = this.checkMeasureOPath();
    const measures: Query = new Query();
    measures.Columns = measureColumns;
    measures.ObjectType = ObjectKeys.MEASURE;
    measures.OPath = measureOPath;

    /*** Latest MeasureProgress ***/
    const measureProgress: Query = new Query();
    measureProgress.Name = "LastMeasureProgress";
    measureProgress.OPath =
      "MeasureProgresses[GetDate(SysDateInsert)=^.MeasureProgresses.Max(GetDate(SysDateInsert))]";
    measureProgress.Columns = [
      this.coreDataService.createQueryColumn("Id", "Id"),
      this.coreDataService.createQueryColumn("Decision", "Decision"),
      this.coreDataService.createQueryColumn(
        "ProgressPercentage",
        "ProgressPercentage",
      ),

      this.dataService.coreDataService.createQueryColumn(
        "Concat(FormatDate(SysDateInsert,'yyyy-mm-dd'),' ',FormatTime( SysTimeInsert,'hh:mm:ss'))",
        "ProgressDate",
        QueryColumnSortOrder.Descending,
      ),
    ];

    /*ApprovalRequest*/
    const ApprovalRequest: Query = new Query();
    ApprovalRequest.Name = "ApprovalRequests";
    ApprovalRequest.OPath = "ApprovalRequests";
    const ApprovalRequestColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn("Id", "Id"),
      this.coreDataService.createQueryColumn("Status", "Status"),
      this.dataService.coreDataService.createQueryColumn(
        "Concat(FormatDate(SysDateUpdate,'yyyy-mm-dd'),' ',FormatTime(SysTimeUpdate,'hh:mm:ss'))",
        "ApprovalRequestDate",
        QueryColumnSortOrder.Descending,
      ),
    ];

    ApprovalRequest.Columns = ApprovalRequestColumns;

    /*** APROVAL STEP */
    const ApprovalSteps: Query = new Query();
    ApprovalSteps.Name = "ApprovalSteps";
    ApprovalSteps.OPath = "ApprovalSteps";
    const ApprovalStepsColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn("Id", "Id"),
      this.coreDataService.createQueryColumn("Decision", "Decision"),
      this.coreDataService.createQueryColumn("SeqNr", "SeqNr"),
      this.dataService.coreDataService.createQueryColumn(
        "Concat(FormatDate(SysDateInsert,'yyyy-mm-dd'),' ',FormatTime( SysTimeInsert,'hh:mm:ss'))",
        "ApprovalStepDate",
        QueryColumnSortOrder.Descending,
      ),
    ];
    ApprovalSteps.Columns = ApprovalStepsColumns;

    ApprovalRequest.ObjectQueries = [ApprovalSteps];
    measureProgress.ObjectQueries = [ApprovalRequest];
    /*** Measure Resources ***/
    const measureResources: Query = new Query();
    measureResources.Name = "MeasureResources";
    measureResources.OPath = "MeasureResources";
    measureResources.Columns = [
      this.coreDataService.createQueryColumn("Id", "Id"),
      this.coreDataService.createQueryColumn("IdRefPerson", "Member"),
      this.coreDataService.createQueryColumn("ref:IdRefPerson", "Person"),
    ];

    measures.ObjectQueries = [measureProgress, measureResources];

    const queryObjectModel = new QueryObjectsModel();
    queryObjectModel.ObjectQueries = [measures];

    return this.coreDataService.executeReadObjectsQuery(queryObjectModel);
  }

  public persistProperty(
    ParentId: string,
    designation: string,
  ): Observable<ArrayBuffer> {
    const propertyColumns: TargetColumnValue[] = [];
    const propertyPersistQuery: TargetObjectData = new TargetObjectData();
    propertyPersistQuery.ObjectKey = new ObjectKey();
    propertyPersistQuery.ObjectKey.ObjectType = ObjectKeys.PROPERTY;

    propertyColumns.push({ Name: "Designation", Value: designation });
    propertyColumns.push({ Name: "Type", Value: "0" });
    propertyColumns.push({ Name: "ParentId", Value: ParentId });
    propertyColumns.push({
      Name: "ParentObjectType",
      Value: ObjectKeys.MEASUREPROGRESS,
    });
    propertyPersistQuery.Mode = PersistMode.Insert;

    propertyPersistQuery.TargetColumns = propertyColumns;
    const persistObject: PersistObjectModel = new PersistObjectModel();
    persistObject.Object = propertyPersistQuery;
    return this.coreDataService.executePersistObjectQuery(persistObject);
  }

  public getStuffMemberDataSource(persons: Persons[]): StuffMemberPerson[] {
    const staffMember = persons.map(
      ({ FirstName, Id, LastName, AddressPerson }) => ({
        FirstName,
        Id,
        LastName,
        Position: AddressPerson?.[0]?.Position || "",
      }),
    );

    return (this.staffMembersDataSource = staffMember.sort((a, b) =>
      a.LastName.localeCompare(b.LastName),
    ));
  }
  public getMeasureResponsibleDataSource(
    persons: Persons[],
  ): StuffMemberPerson[] {
    const staffMember = persons.map(
      ({ FirstName, Id, LastName, AddressPerson }) => ({
        FirstName,
        Id,
        LastName,
        Position: AddressPerson?.[0]?.Position || "",
      }),
    );

    return (this.measureResponsibleDataSource = staffMember
      .filter((t) =>
        [
          ROLES.MeasureManager,
          ROLES.CoreTeam,
          ROLES.SubprojectDeputy,
          ROLES.SubprojectResponsible,
          ROLES.STRATEGIETEM,
          ROLES.Admin,
        ].includes(t.Position as ROLES),
      )
      .sort((a, b) => a.LastName.localeCompare(b.LastName)));
  }

  public getCoreTeamSupervisorDataSource(
    persons: Persons[],
  ): StuffMemberPerson[] {
    const staffMember = persons.map(
      ({ FirstName, Id, LastName, AddressPerson }) => ({
        FirstName,
        Id,
        LastName,
        Position: AddressPerson?.[0]?.Position || "",
      }),
    );

    return staffMember
      .filter((t) =>
        [ROLES.CoreTeam, ROLES.SteeringCommittee, ROLES.Admin].includes(
          t.Position as ROLES,
        ),
      )
      .sort((a, b) => a.LastName.localeCompare(b.LastName));
  }

  public checkAuthorization(): boolean {
    for (let role of this.dataService.userRole) {
      switch (role) {
        case this.ROLES.MeasureParticipant:
          return true;
        default:
          return false;
      }
    }

    return false;
  }

  public updateMeasureRating(measure: IRating) {
    const ratingColumns: TargetColumnValue[] = [
      { Name: "Effort", Value: measure.Effort },
      { Name: "Benefit", Value: measure.Benefit },
      { Name: "TotalScore", Value: measure.TotalScore },
      { Name: "Impact", Value: measure.Impact },
      { Name: "Cost", Value: measure.Cost },
    ];
    const ratingPersistQuery: TargetObjectData = new TargetObjectData();
    ratingPersistQuery.ObjectKey = new ObjectKey();
    ratingPersistQuery.ObjectKey.ObjectType = ObjectKeys.MEASURE;
    ratingPersistQuery.TargetColumns = ratingColumns;
    const persistObject: PersistObjectModel = new PersistObjectModel();
    persistObject.Object = ratingPersistQuery;

    if (measure.measureId) {
      ratingPersistQuery.Mode = PersistMode.Update;
      ratingPersistQuery.ObjectKey.Id = measure.measureId;
    } else {
      ratingPersistQuery.Mode = PersistMode.Insert;
    }

    const persistRatingObject: PersistObjectModel = new PersistObjectModel();
    persistRatingObject.Object = ratingPersistQuery;
    return this.coreDataService.executePersistObjectQuery(persistRatingObject);
  }

  updateMeasureRatingComment(measure: IRating, commentId: string) {
    const commentColumns: TargetColumnValue[] = [
      { Name: "Text", Value: measure.comment },
      { Name: "ParentId", Value: measure.measureId },
      { Name: "ParentObjectType", Value: ObjectKeys.MEASURE },
      { Name: "Identifier", Value: "MeasureRatingComment" },
      { Name: "IdRefPersonInsert", Value: measure.IdPerson },
    ];
    const commentPersistQuery: TargetObjectData = new TargetObjectData();
    commentPersistQuery.ObjectKey = new ObjectKey();
    commentPersistQuery.ObjectKey.ObjectType = ObjectKeys.COMMENT;
    commentPersistQuery.TargetColumns = commentColumns;
    commentPersistQuery.TargetColumns;
    const persistObject: PersistObjectModel = new PersistObjectModel();
    persistObject.Object = commentPersistQuery;

    if (commentId) {
      commentPersistQuery.Mode = PersistMode.Update;
      commentPersistQuery.ObjectKey.Id = commentId;
    } else {
      commentPersistQuery.Mode = PersistMode.Insert;
    }

    const persistCommentObject: PersistObjectModel = new PersistObjectModel();
    persistCommentObject.Object = commentPersistQuery;
    return this.coreDataService.executePersistObjectQuery(persistCommentObject);
  }
  public get locale(): string {
    const lang = sessionStorage.getItem("currentUserLanguage");
    return lang === "de" ? "de-DE" : "en-US";
  }
}
